import { Clock, ListChecks, Play } from 'lucide-react';
import { EDUCATE_TYPE_LABEL } from '@/utils/units';
import type { EducateStoryline, EducateProgress } from '@/types/educate';

interface WelcomeScreenProps {
  storyline: EducateStoryline;
  progress: EducateProgress | null;
  unitCount: number;
  onStart: () => void;
}

export function WelcomeScreen({ storyline, progress, unitCount, onStart }: WelcomeScreenProps) {
  const { title, description, objectives, timerConfig } = storyline.uiContent;
  const resuming = (progress?.progress?.completedUnits?.length ?? 0) > 0;
  const minutes = timerConfig?.timeLimit ? Math.round(timerConfig.timeLimit / 60) : null;

  return (
    <section className="space-y-8" data-testid="welcome-screen">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">
          {EDUCATE_TYPE_LABEL[storyline.educateType]}
        </p>
        <h2 className="mt-2 text-3xl font-semibold">{title}</h2>
        {description && (
          <p className="mt-3 max-w-prose text-slate-600 dark:text-slate-400">{description}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600 dark:text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <ListChecks size={16} /> {unitCount} {unitCount === 1 ? 'step' : 'steps'}
        </span>
        {minutes && (
          <span className="inline-flex items-center gap-1.5">
            <Clock size={16} /> about {minutes} min
          </span>
        )}
      </div>

      {objectives.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            What you will learn
          </h3>
          <ul className="mt-3 space-y-2">
            {objectives.map((objective, index) => (
              <li key={index} className="flex gap-2 text-slate-700 dark:text-slate-300">
                <span className="text-brand-500">•</span>
                <span>{objective}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={onStart}
        className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-5 py-2.5 font-medium text-white transition hover:bg-brand-700"
      >
        <Play size={18} />
        {resuming ? 'Resume' : 'Start'}
      </button>
    </section>
  );
}
